/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { useCosmosComponentContext } from "@components/context";
import { useHygraphImageLoader } from "@components/hooks";
import {
	CosmosCard,
	CosmosDesc,
	CosmosImage,
	CosmosLink,
	CosmosText,
	CosmosTextVariantEnum,
	CosmosTitle,
} from "@components/elements";
import { getFeedCardHref } from "@components/elements/molecules/feed-cards/feed-card.utils";
import type { AnalyticsProps } from "@components/types";
import { formatTimeAndDate } from "@components/utils";
import type { NewsCardFragment } from "@cosmos/hygraph-schema";
import { Avatar } from "@eds/react/Avatar";
import { Tag } from "@eds/react/Tag";
import { useLocale } from "@epic-mw/i18n";
import { css, cx } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { useMemo } from "react";
import { newsCard } from "./NewsCard.style";

// override EDS styles
const badge = css({
	backgroundColor: "token(eds.fill.secondary.elveated.default) !important",
	"&:hover": {
		backgroundColor: "token(eds.fill.secondary.elevated.hover) !important",
	},
});

export type NewsCardProps = NewsCardFragment &
	AnalyticsProps & { tag: { tagId?: string }; originUrl?: string; date?: string | null; hideCategoryPills?: boolean };

const newsCardEyebrpwStyles = css({ color: "eds.text.secondary" });
const newsCardTitleStyles = css({ fontWeight: "bold" });

export function NewsCard({
	image,
	href,
	title,
	description,
	tag,
	authorTag,
	analytics,
	originUrl,
	date,
	hideCategoryPills,
}: NewsCardProps): React.JSX.Element {
	const styles = newsCard();
	const locale = useLocale();
	const hygraphImageLoader = useHygraphImageLoader();
	const { feedRootPath = "/feed", locales, feedBrand } = useCosmosComponentContext();

	let tagHref: string;
	if (tag?.tagId) {
		tagHref = `/${locale}${feedRootPath}?tags=${tag?.tagId}`;
	} else {
		tagHref = tag?.href || "";
	}

	const formattedDate = useMemo(() => {
		if (!date) return "";
		try {
			return formatTimeAndDate(date, locale);
		} catch {
			console.warn("[NewsCard] Invalid date");
		}
		return "";
	}, [date, locale]);

	const showCategoryPill = Boolean(!hideCategoryPills && tag?.label && tagHref);

	return (
		<CosmosCard className={cx("news-card", styles.wrapper)} padding={12} variant="material" direction="row">
			{image?.url ? (
				<CosmosCard.Thumbnail className={cx("image-box", styles["image-box"])}>
					<CosmosImage
						className={cx("image", styles.image)}
						src={image?.url}
						width={263}
						height={148}
						loading="lazy"
						alt={title?.text || "News card"}
						loader={hygraphImageLoader}
						sizes="(max-width: 639px) 96px, (max-width: 767px) 229px, (max-width: 1023px) 148px, 263px"
					/>
				</CosmosCard.Thumbnail>
			) : null}
			<VStack
				overflow="hidden"
				alignItems="flex-start"
				justifyContent="space-between"
				padding="eds.dimension.8"
				gap="eds.dimension.16"
				flex="1"
				minWidth="0"
			>
				<VStack alignItems="flex-start" gap="eds.dimension.12">
					{formattedDate ? (
						<CosmosText
							className={cx("news-card-eyebrow", newsCardEyebrpwStyles)}
							text={formattedDate}
							variant={CosmosTextVariantEnum.eyebrowSM}
						/>
					) : null}
					<VStack alignItems="flex-start" gap="eds.dimension.8">
						{title?.html ? (
							<CosmosCard.Link
								href={getFeedCardHref(locale, locales, { url: href || "", originUrl }, feedBrand)}
								analytics={analytics}
							>
								<CosmosTitle
									title={{ html: title?.html, text: title?.text, variant: CosmosTextVariantEnum.paragraphMD, as: "h3" }}
									className={cx("news-card-title", newsCardTitleStyles)}
								/>
							</CosmosCard.Link>
						) : (
							""
						)}
						<CosmosDesc
							description={{
								html: description?.html,
								variant: CosmosTextVariantEnum.paragraphSM,
								color: "eds.text.secondary",
							}}
							className="news-card-description"
						/>
					</VStack>
				</VStack>
				{showCategoryPill || authorTag ? (
					<HStack alignItems={{ base: "start", sm: "center" }} gap="eds.dimension.16" fontSize="0">
						{showCategoryPill ? <Tag as={CosmosLink} className={badge} href={tagHref} label={tag?.label} /> : ""}
						{authorTag ? (
							<Avatar
								as={CosmosLink}
								href={authorTag?.href || ""}
								image={authorTag?.image?.url}
								label={authorTag?.label}
								size="sm"
							/>
						) : (
							""
						)}
					</HStack>
				) : (
					""
				)}
			</VStack>
		</CosmosCard>
	);
}
